import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, RefreshCw, User, Bot } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch {
    return ts;
  }
}

function formatDate(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default function ConversationLog() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 15000); // poll every 15s
    return () => clearInterval(interval);
  }, []);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/api/conversations/messages`, { withCredentials: true });
      setMessages(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMessages();
    setRefreshing(false);
  };

  return (
    <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] overflow-hidden transition-all duration-200 hover:border-[#7B2FFF]/30">
      {/* Terminal header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#27272A] bg-[#1A1A1A]">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#7B2FFF]" />
          <h3 className="text-sm font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Conversation Log
          </h3>
          <div className="flex items-center gap-1.5 ml-2">
            <div className="w-2 h-2 rounded-full bg-[#7B2FFF] pulse-glow" />
            <span className="text-xs text-[#71717A] font-mono">LIVE</span>
          </div>
        </div>
        <button
          data-testid="refresh-conversations"
          onClick={handleRefresh}
          className="p-1.5 rounded-md text-[#71717A] hover:text-[#EDEDF0] hover:bg-[#ffffff08] transition-all duration-200"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Terminal body */}
      <div className="terminal-bg relative">
        <ScrollArea className="h-[360px]" ref={scrollRef}>
          <div className="p-4 space-y-1">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-60 gap-3">
                <div className="w-6 h-6 border-2 border-[#7B2FFF] border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-[#71717A] font-mono">Loading messages...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-60 gap-4" data-testid="empty-conversation-log">
                <div className="relative">
                  <img
                    src="https://images.unsplash.com/photo-1754738381730-e08d0a402f0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBwdXJwbGUlMjB0ZWNobm9sb2d5JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzUwMjExNDF8MA&ixlib=rb-4.1.0&q=85"
                    alt="No messages"
                    className="w-24 h-24 rounded-lg opacity-20 grayscale object-cover"
                  />
                </div>
                <div className="text-center">
                  <p className="text-sm text-[#A1A1AA]">No messages yet</p>
                  <p className="text-xs text-[#71717A] mt-1 font-mono">
                    Messages will appear here when your bot handles conversations
                  </p>
                </div>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div
                  key={i}
                  data-testid={`message-${i}`}
                  className={`flex gap-3 py-2 px-3 rounded-md text-sm transition-all duration-150 hover:bg-[#ffffff04] ${
                    msg.role === 'bot' ? 'border-l-2 border-l-[#7B2FFF]' : ''
                  }`}
                >
                  <div className="shrink-0 pt-0.5">
                    {msg.role === 'bot' ? (
                      <Bot className="w-4 h-4 text-[#7B2FFF]" />
                    ) : (
                      <User className="w-4 h-4 text-[#A1A1AA]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-medium text-[#A1A1AA]">
                        {msg.role === 'bot' ? 'AI Bot' : msg.contact_name || 'Contact'}
                      </span>
                      <span className="text-xs text-[#71717A] font-mono">
                        {formatDate(msg.timestamp)} {formatTime(msg.timestamp)}
                      </span>
                    </div>
                    <p className="text-[#EDEDF0] font-mono text-sm leading-relaxed break-words">
                      {msg.content}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
